import { Icon } from '@socialchorus/shared-ui-components';
import { useCallback } from 'react';
import { useHistory } from 'react-router';

import { UserSuggestion } from '../../common/use-user-suggestions';
import { Link } from '../ui';
import { MentionAvatar } from './mention-avatar';

import './mention-menu.scss';

export interface MentionMenuItemData {
  id: string;
  name: string;
  avatar: UserSuggestion['avatar'];
  displayAttributes: string[];
}

/**
 * Keyboard path to a suggestion's profile. The profile link is a mouse
 * affordance only (`tabIndex={-1}`): focus stays in the editor while the list is
 * open and Tab commits the highlighted row, so the link can never be reached by
 * Tab. Alt+Enter on the highlighted row opens the profile instead.
 */
export const isOpenProfileShortcut = (e: {
  key: string;
  altKey: boolean;
}): boolean => e.key === 'Enter' && e.altKey;

export function useOpenMentionProfile(): (userId: string) => void {
  const history = useHistory();
  return useCallback(
    (userId: string) => {
      window.open(
        history.createHref({ pathname: `/profiles/${userId}` }),
        '_blank',
        'noopener,noreferrer'
      );
    },
    [history]
  );
}

interface MentionMenuItemProps {
  /** DOM id, referenced by the editor's `aria-activedescendant`. */
  id?: string;
  data: MentionMenuItemData;
  isSelected: boolean;
  onSelect: () => void;
  onHover: () => void;
}

/**
 * A single user suggestion row, shared by the Lexical body editor's mention
 * plugin and the reference text input so both render identically. Markup mirrors
 * the original `MentionMenuItem` in `mention-plugin.tsx`.
 */
export function MentionMenuItem({
  id,
  data,
  isSelected,
  onSelect,
  onHover,
}: MentionMenuItemProps) {
  const history = useHistory();
  const profileUrl = history.createHref({ pathname: `/profiles/${data.id}` });

  return (
    <div
      id={id}
      className={`lexical-mention-entry${
        isSelected ? ' lexical-mention-entry--active' : ''
      }`}
      role="option"
      aria-selected={isSelected ? true : undefined}
      // The editor keeps focus and drives the list (ARIA editable combobox), so
      // rows are not Tab stops.
      tabIndex={-1}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') onSelect();
      }}
      // mousemove, not mouseenter: arrow-key scrolling slides rows under a
      // parked pointer, and mouseenter would then steal the highlight back.
      onMouseMove={() => {
        if (!isSelected) onHover();
      }}
      ref={(el) => {
        if (isSelected && el) {
          el.scrollIntoView({ block: 'nearest' });
        }
      }}
    >
      <div className="lexical-mention-avatar">
        <MentionAvatar
          name={data.name}
          avatarUrl={data.avatar.url}
          avatarColor={data.avatar.color}
        />
      </div>
      <div className="lexical-mention-entry-text">
        <div className="lexical-mention-name">{data.name}</div>
        {data.displayAttributes.length > 0 && (
          <div className="lexical-mention-details">
            {data.displayAttributes.join(' • ')}
          </div>
        )}
      </div>
      <Link
        href={profileUrl}
        target="_blank"
        className="lexical-mention-profile-link"
        tabIndex={-1}
        aria-label={`View ${data.name}'s profile (opens in a new tab)`}
        // Nested inside the row's own onClick/onKeyDown (which select this
        // suggestion), so without stopping propagation, opening the profile
        // also inserts the mention. Stop both the click and the Enter/Space
        // keydown the row listens for from bubbling up.
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
        }}
      >
        <Icon size={24}>open_in_new</Icon>
      </Link>
    </div>
  );
}
